import React, { FunctionComponent } from 'react';

import styled, { useTheme } from 'styled-components';

import { Translate } from 'react-localize-redux';

import Flex from '@components/layout/flex';
import Grid from '@components/basics/grid';
import Typography from '@components/basics/typography';
import CompletedIcon from '@components/basics/icons/achievements-icons/completed';
import DineroFactory from 'dinero.js';
import { Product } from '@services/product/types/product';

interface TracProTableContentsProps {
  storesCount: number;
  proPlan?: Product;
}

export const TracProTableContents: FunctionComponent<
  TracProTableContentsProps
> = ({ storesCount, proPlan }) => {
  const theme = useTheme();

  const handlePlanPrice = (value: number) => {
    const price = DineroFactory({
      amount: Math.ceil(value / 12),
      currency: 'USD',
      precision: 2,
    });

    return price.toFormat('$0,0.00');
  };

  return (
    <Table className="mb-40">
      <Row>
        <Typography className="col-1" weight="700" type="small">
          <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.MENU.TABLE.TIMELINE" />
        </Typography>
        <Typography className="col-3" type="small" align="center">
          <Translate
            id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.MENU.TABLE.TIMELINE.CONTENT"
            data={{ amount: storesCount, time: proPlan?.releaseTimeline || '' }}
          />
        </Typography>
      </Row>
      <Row>
        <Typography className="col-1" weight="700" type="small">
          <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.MENU.TABLE.PAYOUT" />
        </Typography>
        <Typography className="col-3" type="small" align="center">
          <Translate
            id={
              proPlan && proPlan.payout
                ? `COMMON.${proPlan.payout.toUpperCase()}`
                : 'COMMON.MONTHLY'
            }
          />
        </Typography>
      </Row>
      <Row>
        <Typography className="col-1" weight="700" type="small">
          <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.MENU.TABLE.ANALYTICS" />
        </Typography>
        <IconWrapper className="col-3">
          <CompletedIcon size={24} />
        </IconWrapper>
      </Row>
      <Row>
        <div className="col-1">
          <Grid gap={5}>
            <Typography weight="700" type="small">
              <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.MENU.TABLE.SYNC_SUPPORT" />
            </Typography>
            <Typography type="small" color={theme.colors.tertiary}>
              <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.MENU.TABLE.SYNC_SUPPORT.TEXT" />
            </Typography>
          </Grid>
        </div>
        <IconWrapper className="col-3">
          <CompletedIcon size={24} />
        </IconWrapper>
      </Row>
      <Row>
        <div className="col-1">
          <Grid gap={5}>
            <Typography weight="700" type="small">
              <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.MENU.TABLE.MERCH" />
            </Typography>
            <Typography type="small" color={theme.colors.tertiary}>
              <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.MENU.TABLE.MERCH.TEXT" />
            </Typography>
          </Grid>
        </div>
        <IconWrapper className="col-3">
          <CompletedIcon size={24} />
        </IconWrapper>
      </Row>
      <Row>
        <div className="col-1">
          <Grid gap={5}>
            <Typography weight="700" type="small">
              <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.MENU.TABLE.LAST_FOREVER" />
            </Typography>
            <Typography type="small" color={theme.colors.tertiary}>
              <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.MENU.TABLE.LAST_FOREVER.TEXT" />
            </Typography>
          </Grid>
        </div>
        <IconWrapper className="col-3">
          <CompletedIcon size={24} />
        </IconWrapper>
      </Row>
      <Row>
        <div className="col-1">
          <Grid gap={5}>
            <Typography weight="700" type="small">
              <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.MENU.TABLE.ADDONS" />
            </Typography>
            <Typography type="small" color={theme.colors.tertiary}>
              <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.MENU.TABLE.ADDONS.TEXT" />
            </Typography>
          </Grid>
        </div>
        <IconWrapper className="col-3">
          <CompletedIcon size={24} />
        </IconWrapper>
      </Row>
      <Grid className="table-footer">
        <Typography className="col-1" type="title">
          <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.MENU.TABLE.TOTAL" />
        </Typography>
        <Grid className="col-3">
          <Flex alignItems="baseline" gap={2} justifyContent="center">
            <Typography weight="700" type="small" align="center">
              {proPlan &&
                handlePlanPrice(
                  proPlan.prices.find((price) => price.frequency === 'year')
                    ?.value || 6000
                )}
              /m
            </Typography>
          </Flex>
          <Typography type="small" align="center" color={theme.colors.tertiary}>
            <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.MENU.TABLE.BILLED" />
          </Typography>
        </Grid>
      </Grid>
    </Table>
  );
};

const Table = styled(Grid)`
  max-width: 410px;
  margin: auto;

  .col-1 {
    grid-column: span 4;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  .col-2 {
    grid-column: 5 / 7;
  }

  .col-3 {
    grid-column: 5 / 8;
    justify-content: center;
  }

  .table-header {
    padding-bottom: 20px;
    position: sticky;
    top: 0;
    background: ${({ theme }) => theme.colors.differentBackground};
  }

  .table-footer {
    margin-top: 20px;
  }

  .table-header,
  .table-footer {
    align-items: center;
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }
`;

const Row = styled(Grid)`
  padding: 10px 0;
  align-items: center;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border-bottom: 1px dashed ${({ theme }) => theme.colors.border};
`;

const IconWrapper = styled(Flex)`
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin: 0 auto;
`;
